import {
  DeleteOutlined,
  EditOutlined,
  FileExcelOutlined,
  FilterOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Popconfirm,
  Result,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import debounce from "lodash/debounce";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import {
  deleteFinalInspection,
  exportFinalInspections,
  fetchFinalInspectionList,
  fetchFinalInspectionStyles,
  fetchInspectionLeadBys,
  fetchInspectionTypes,
  isRequestCancelled,
} from "apiServices/finalProductInspectionService";
import {
  FINAL_PRODUCT_INSPECTION_CREATE_PERMISSION,
  FINAL_PRODUCT_INSPECTION_DELETE_PERMISSION,
  FINAL_PRODUCT_INSPECTION_EDIT_PERMISSION,
} from "routes/permissions";
import { hasPermission } from "utils/functions";
import {
  ADD_PATH,
  DATE_FORMAT,
  DISPLAY_DATE_FORMAT,
  EDIT_PATH,
  INSPECTION_STATUS_OPTIONS,
  STYLE_PAGE_SIZE,
  STYLE_SEARCH_DEBOUNCE_MS,
} from "./constants";
import useActiveList from "./useActiveList";
import "./finalInspection.scss";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const DEFAULT_SORT = { sort: "id", direction: "desc" };

/** scroll.y switches antd to table-layout: fixed, so every leaf column needs an explicit width. */
const COLUMN_WIDTHS = {
  sl: 60,
  inspection_date: 130,
  style: 140,
  buyer: 160,
  po: 140,
  sub_style: 120,
  color: 120,
  inspection_type: 140,
  inspection_lead_by: 130,
  ship_date: 120,
  ship_qty: 100,
  inspection_qty: 120,
  sample_qty: 100,
  aql_value: 85,
  critical_qty: 85,
  major_qty: 85,
  minor_qty: 85,
  total_defect_qty: 100,
  oql_value: 90,
  inspection_status: 100,
  report_link: 90,
  action: 100,
};

const formatDate = (value) => (value ? moment(value).format(DISPLAY_DATE_FORMAT) : "");

const toFilterParams = (values) => ({
  style_id: values.style_id,
  inspection_type_id: values.inspection_type_id,
  inspection_lead_by_id: values.inspection_lead_by_id,
  inspection_status: values.inspection_status,
  date_from: values.inspection_date_range?.[0]?.format(DATE_FORMAT),
  date_to: values.inspection_date_range?.[1]?.format(DATE_FORMAT),
});

const countActiveFilters = (filters) => Object.values(filters).filter((value) => value !== undefined && value !== null && value !== "").length;

const FinalInspection = () => {
  const navigate = useNavigate();
  const canCreate = hasPermission([FINAL_PRODUCT_INSPECTION_CREATE_PERMISSION]);
  const canEdit = hasPermission([FINAL_PRODUCT_INSPECTION_EDIT_PERMISSION]);
  const canDelete = hasPermission([FINAL_PRODUCT_INSPECTION_DELETE_PERMISSION]);

  const [filterForm] = Form.useForm();
  const [showFilter, setShowFilter] = useState(false);

  const [sorter, setSorter] = useState(DEFAULT_SORT);
  const [filters, setFilters] = useState({});
  const [reloadKey, setReloadKey] = useState(0);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  const [inspectionTypes] = useActiveList(fetchInspectionTypes, "Failed to load inspection types.");
  const [leadBys] = useActiveList(fetchInspectionLeadBys, "Failed to load inspection lead by list.");

  const [styleOptions, setStyleOptions] = useState([]);
  const [styleLoading, setStyleLoading] = useState(false);
  const styleRequestRef = useRef(0);

  // Prevent outer page-level horizontal & vertical scrollbars while on Final Inspection
  useEffect(() => {
    const siteLayout = document.querySelector(".site-layout");
    const prevSiteOverflow = siteLayout ? siteLayout.style.overflow : "";
    const prevBodyOverflowX = document.body.style.overflowX;

    if (siteLayout) {
      siteLayout.classList.add("final-inspection-site-layout");
      siteLayout.style.overflow = "hidden";
    }
    document.body.style.overflowX = "hidden";

    return () => {
      if (siteLayout) {
        siteLayout.classList.remove("final-inspection-site-layout");
        siteLayout.style.overflow = prevSiteOverflow;
      }
      document.body.style.overflowX = prevBodyOverflowX;
    };
  }, []);

  // Prevent browser contextmenu on the table/scrollbar from popping up and triggering reload
  useEffect(() => {
    const listEl = document.querySelector(".final-inspection-list");
    const preventContextMenu = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    if (listEl) {
      listEl.addEventListener("contextmenu", preventContextMenu, { capture: true });
    }
    return () => {
      if (listEl) {
        listEl.removeEventListener("contextmenu", preventContextMenu, { capture: true });
      }
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchFinalInspectionList({ perPage: 10000, ...sorter, filters }, controller.signal)
      .then((result) => {
        setRows(result.rows);
      })
      .catch((err) => {
        if (!isRequestCancelled(err)) setError(err?.message || "Failed to load final inspections.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [sorter, filters, reloadKey]);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  /* ------------------------------ Style filter search ------------------------------ */

  const loadStyles = useCallback(async (search) => {
    const requestId = ++styleRequestRef.current;
    setStyleLoading(true);
    try {
      const result = await fetchFinalInspectionStyles({ search, page: 1, perPage: STYLE_PAGE_SIZE });
      if (requestId === styleRequestRef.current) setStyleOptions(result.items);
    } catch (err) {
      if (requestId === styleRequestRef.current) message.error(err?.message || "Failed to load styles.");
    } finally {
      if (requestId === styleRequestRef.current) setStyleLoading(false);
    }
  }, []);

  const debouncedStyleSearch = useMemo(() => debounce(loadStyles, STYLE_SEARCH_DEBOUNCE_MS), [loadStyles]);

  useEffect(() => {
    if (showFilter && styleOptions.length === 0) loadStyles("");
  }, [showFilter, styleOptions.length, loadStyles]);

  useEffect(() => () => debouncedStyleSearch.cancel(), [debouncedStyleSearch]);

  /* ------------------------------------ Filters ------------------------------------ */

  const handleFilterSearch = (values) => {
    setFilters(toFilterParams(values));
  };

  const handleFilterReset = () => {
    filterForm.resetFields();
    setFilters({});
  };

  const activeFilterCount = countActiveFilters(filters);

  /* ------------------------------------ Actions ------------------------------------ */

  const handleExport = async () => {
    setExporting(true);
    try {
      const exportParams = {
        ...filters,
        sort: sorter?.sort,
        direction: sorter?.direction,
      };
      const result = await exportFinalInspections(exportParams);
      if (result.ok) {
        message.success(result.message || "Final inspection report exported successfully.");
      } else {
        message.error(result.message);
      }
    } catch (err) {
      message.error(err?.message || "Failed to export final inspection report.");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = useCallback(
    async (record) => {
      const result = await deleteFinalInspection(record.id);
      if (result.ok) {
        message.success(result.message || "Final inspection deleted.");
      } else {
        message.error(result.message);
      }
      refetch();
    },
    [refetch],
  );

  const handleTableChange = (_pagination, _filters, tableSorter) => {
    const field = tableSorter?.column?.sortKey;
    const direction = tableSorter?.order === "ascend" ? "asc" : tableSorter?.order === "descend" ? "desc" : null;
    setSorter((prev) => {
      const nextSort = field && direction ? field : DEFAULT_SORT.sort;
      const nextDir = field && direction ? direction : DEFAULT_SORT.direction;
      if (prev.sort === nextSort && prev.direction === nextDir) {
        return prev;
      }
      return { sort: nextSort, direction: nextDir };
    });
  };

  const sortOrderFor = useCallback(
    (key) =>
      sorter.sort === key && sorter !== DEFAULT_SORT ? (sorter.direction === "asc" ? "ascend" : "descend") : null,
    [sorter],
  );

  const sortableColumn = useCallback(
    (key) => ({ sortKey: key, sorter: true, sortOrder: sortOrderFor(key) }),
    [sortOrderFor],
  );

  const columns = useMemo(
    () => [
      {
        title: "SL",
        key: "sl",
        width: COLUMN_WIDTHS.sl,
        align: "center",
        fixed: "left",
        render: (_, __, index) => index + 1,
      },
      {
        title: "Inspection Date",
        dataIndex: "inspection_date",
        key: "inspection_date",
        width: COLUMN_WIDTHS.inspection_date,
        ...sortableColumn("inspection_date"),
        render: formatDate,
      },
      {
        title: "Style",
        key: "style",
        width: COLUMN_WIDTHS.style,
        ellipsis: true,
        render: (_, row) => row.style?.style_no,
      },
      {
        title: "Buyer",
        key: "buyer",
        width: COLUMN_WIDTHS.buyer,
        ellipsis: true,
        render: (_, row) => row.buyer?.name || row.sub_contract_factory?.name,
      },
      {
        title: "PO",
        key: "po",
        width: COLUMN_WIDTHS.po,
        ellipsis: true,
        render: (_, row) => {
          let text = "-";
          if (Array.isArray(row.po_maps) && row.po_maps.length > 0) {
            const pos = row.po_maps
              .map((p) => p.po_map?.original_po || p.po_map?.internal_po)
              .filter(Boolean);
            if (pos.length > 0) text = pos.join(", ");
          } else if (row.po_map?.original_po || row.po_map?.internal_po) {
            text = row.po_map?.original_po || row.po_map?.internal_po;
          }
          return (
            <Tooltip title={text !== "-" ? text : undefined}>
              <span>{text}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Sub Style",
        key: "sub_style",
        width: COLUMN_WIDTHS.sub_style,
        ellipsis: true,
        render: (_, row) => row.sub_style?.sub_style_no,
      },
      {
        title: "Color",
        key: "color",
        width: COLUMN_WIDTHS.color,
        ellipsis: true,
        render: (_, row) => row.color?.name,
      },
      {
        title: "Inspection Type",
        key: "inspection_type",
        width: COLUMN_WIDTHS.inspection_type,
        ellipsis: true,
        render: (_, row) => row.inspection_type?.name,
      },
      {
        title: "Lead By",
        key: "inspection_lead_by",
        width: COLUMN_WIDTHS.inspection_lead_by,
        ellipsis: true,
        render: (_, row) => row.inspection_lead_by?.name,
      },
      {
        title: "Ship Date",
        dataIndex: "ship_date",
        key: "ship_date",
        width: COLUMN_WIDTHS.ship_date,
        ...sortableColumn("ship_date"),
        render: formatDate,
      },
      {
        title: "Ship Qty",
        dataIndex: "ship_qty",
        key: "ship_qty",
        width: COLUMN_WIDTHS.ship_qty,
        align: "right",
        ...sortableColumn("ship_qty"),
      },
      {
        title: "Inspection Qty",
        dataIndex: "inspection_qty",
        key: "inspection_qty",
        width: COLUMN_WIDTHS.inspection_qty,
        align: "right",
        ...sortableColumn("inspection_qty"),
      },
      {
        title: "Sample Qty",
        dataIndex: "sample_qty",
        key: "sample_qty",
        width: COLUMN_WIDTHS.sample_qty,
        align: "right",
        ...sortableColumn("sample_qty"),
      },
      {
        title: "AQL",
        dataIndex: "aql_value",
        key: "aql_value",
        width: COLUMN_WIDTHS.aql_value,
        align: "right",
        ...sortableColumn("aql_value"),
      },
      {
        title: "Critical",
        dataIndex: "critical_qty",
        key: "critical_qty",
        width: COLUMN_WIDTHS.critical_qty,
        align: "right",
      },
      {
        title: "Major",
        dataIndex: "major_qty",
        key: "major_qty",
        width: COLUMN_WIDTHS.major_qty,
        align: "right",
      },
      {
        title: "Minor",
        dataIndex: "minor_qty",
        key: "minor_qty",
        width: COLUMN_WIDTHS.minor_qty,
        align: "right",
      },
      {
        title: "Total Defect",
        dataIndex: "total_defect_qty",
        key: "total_defect_qty",
        width: COLUMN_WIDTHS.total_defect_qty,
        align: "right",
      },
      {
        title: "OQL (%)",
        dataIndex: "oql_value",
        key: "oql_value",
        width: COLUMN_WIDTHS.oql_value,
        align: "right",
      },
      {
        title: "Status",
        dataIndex: "inspection_status",
        key: "inspection_status",
        width: COLUMN_WIDTHS.inspection_status,
        align: "center",
        ...sortableColumn("inspection_status"),
        render: (status) =>
          status ? <Tag color={String(status).toLowerCase() === "passed" ? "success" : "error"}>{status}</Tag> : null,
      },
      {
        title: "Report",
        dataIndex: "report_link",
        key: "report_link",
        width: COLUMN_WIDTHS.report_link,
        align: "center",
        render: (link) =>
          link ? (
            <a href={link} target="_blank" rel="noopener noreferrer">
              <LinkOutlined /> View
            </a>
          ) : null,
      },
      {
        title: "Action",
        key: "action",
        fixed: "right",
        width: COLUMN_WIDTHS.action,
        align: "center",
        render: (_, row) => (
          <Space>
            <Tooltip title={canEdit ? "Edit" : "No permission"}>
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={!canEdit}
                onClick={() => navigate(`${EDIT_PATH}/${row.id}`)}
              />
            </Tooltip>
            <Popconfirm
              title="Delete this final inspection?"
              okText="Delete"
              okButtonProps={{ danger: true }}
              disabled={!canDelete}
              onConfirm={() => handleDelete(row)}
            >
              <Tooltip title={canDelete ? "Delete" : "No permission"}>
                <Button size="small" danger icon={<DeleteOutlined />} disabled={!canDelete} />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [canDelete, canEdit, handleDelete, navigate, sortableColumn],
  );

  const scrollX = useMemo(
    () => Object.values(COLUMN_WIDTHS).reduce((sum, w) => sum + w, 0),
    [],
  );

  const scrollY = useMemo(() => {
    if (rows.length === 0) return undefined;
    return showFilter ? "calc(100vh - 460px)" : "calc(100vh - 340px)";
  }, [rows.length, showFilter]);

  return (
    <AppContent
      header={
        <AppPageHeader
          title={
            <Title level={5} style={{ margin: 0 }}>
              Final Inspection Report Summary
            </Title>
          }
          extra={
            <Space>
              <Badge count={activeFilterCount} size="small">
                <Button
                  size="small"
                  type={showFilter ? "danger" : "primary"}
                  icon={<FilterOutlined />}
                  onClick={() => setShowFilter((prev) => !prev)}
                >
                  {`${showFilter ? "Close" : "Open"} Filter Option`}
                </Button>
              </Badge>
              <Button size="small" icon={<ReloadOutlined />} onClick={refetch} disabled={loading}>
                Refresh
              </Button>
              <Button
                size="small"
                icon={<FileExcelOutlined />}
                onClick={handleExport}
                loading={exporting}
                style={{
                  backgroundColor: "#217346",
                  borderColor: "#217346",
                  color: "#ffffff",
                }}
              >
                Export Excel
              </Button>
              <Button
                size="small"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate(ADD_PATH)}
                disabled={!canCreate}
              >
                {canCreate ? "Add New" : "No Permission"}
              </Button>
            </Space>
          }
        />
      }
      content={
        <div
          className="final-inspection-list"
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {showFilter && (
            <Card size="small" className="fpi-filter-card">
              <Form form={filterForm} layout="vertical" size="small" onFinish={handleFilterSearch}>
                <Row gutter={8}>
                  <Col xs={24} md={6}>
                    <Form.Item name="inspection_date_range" label="Inspection Date">
                      <RangePicker style={{ width: "100%" }} format={DATE_FORMAT} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="style_id" label="Style">
                      <Select
                        showSearch
                        allowClear
                        placeholder="Search style no."
                        filterOption={false}
                        onSearch={debouncedStyleSearch}
                        loading={styleLoading}
                        notFoundContent={styleLoading ? <Spin size="small" /> : "No styles found"}
                        options={styleOptions.map((style) => ({ value: Number(style.id), label: style.style_no }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Item name="inspection_type_id" label="Inspection Type">
                      <Select
                        allowClear
                        placeholder="Select type"
                        options={inspectionTypes.map((type) => ({ value: type.id, label: type.name }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Item name="inspection_lead_by_id" label="Lead By">
                      <Select
                        allowClear
                        placeholder="Select lead by"
                        options={leadBys.map((leadBy) => ({ value: leadBy.id, label: leadBy.name }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Item name="inspection_status" label="Status">
                      <Select allowClear placeholder="Select status" options={INSPECTION_STATUS_OPTIONS} />
                    </Form.Item>
                  </Col>
                </Row>
                <Space>
                  <Button type="primary" size="small" htmlType="submit" icon={<SearchOutlined />}>
                    Search
                  </Button>
                  <Button size="small" onClick={handleFilterReset}>
                    Reset
                  </Button>
                </Space>
              </Form>
            </Card>
          )}

          {error ? (
            <Result
              status="error"
              title="Could not load final inspections"
              subTitle={error}
              extra={
                <Button icon={<ReloadOutlined />} onClick={refetch}>
                  Retry
                </Button>
              }
            />
          ) : (
            <Table
              className="final-inspection-table"
              rowKey="id"
              size="small"
              bordered
              loading={loading}
              columns={columns}
              dataSource={rows}
              scroll={{
                x: scrollX,
                y: scrollY,
              }}
              locale={{
                emptyText: (
                  <div style={{ padding: "40px 0" }}>
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No final inspections found">
                      {canCreate && (
                        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => navigate(ADD_PATH)}>
                          Add New
                        </Button>
                      )}
                    </Empty>
                  </div>
                ),
              }}
              onChange={handleTableChange}
              pagination={false}
            />
          )}
        </div>
      }
    />
  );
};

export default FinalInspection;
